import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';

const Terms = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 bg-maritime-primary">
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 bg-maritime-accent/20 backdrop-blur-sm border border-maritime-accent/30 rounded-full px-4 py-2 mb-6">
              <Scale className="w-4 h-4 text-maritime-accent" />
              <span className="text-sm text-white font-medium">Legal Information</span>
            </div>
            <h1 className="text-5xl font-bold text-white mb-6">Terms & Conditions</h1>
            <p className="text-xl text-gray-200 leading-relaxed">
              Please read these terms and conditions carefully before using our services
            </p>
          </div>
        </div>
      </section>

      {/* Content Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            {/* Last Updated */}
            <Card className="mb-8 border-l-4 border-maritime-accent">
              <CardContent className="p-6">
                <div className="flex items-center gap-3">
                  <FileText className="w-5 h-5 text-maritime-accent" />
                  <div>
                    <p className="font-semibold text-maritime-primary">Last Updated</p>
                    <p className="text-sm text-gray-600">December 2024</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Terms Content */}
            <div className="prose prose-lg max-w-none">
              
              {/* 1. Introduction */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">1. Introduction</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Welcome to Gamma Marine Limited ("Company", "we", "our", "us"). These Terms and Conditions 
                  ("Terms") govern your use of our marine survey and inspection services and our website.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  By engaging our services or using our website, you agree to be bound by these Terms. 
                  If you disagree with any part of these terms, please do not use our services.
                </p>
              </section>

              {/* 2. Services */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">2. Services Provided</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  Gamma Marine Limited provides professional marine survey and inspection services including, 
                  but not limited to:
                </p>
                <ul className="list-disc pl-6 text-gray-600 space-y-2 mb-4">
                  <li>Vessel condition surveys</li>
                  <li>Lifting gear inspections</li>
                  <li>Container and cargo inspections</li>
                  <li>On-hire and off-hire inspections</li>
                  <li>Accident and damage investigations</li>
                  <li>Pre-purchase inspections</li>
                  <li>Bunker surveys</li>
                  <li>Technical assistance and consultation</li>
                </ul>
                <p className="text-gray-600 leading-relaxed">
                  All services are provided in accordance with international maritime standards and 
                  Maritime New Zealand regulations.
                </p>
              </section>

              {/* 3. Service Agreement */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">3. Service Agreement</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>3.1 Engagement:</strong> Services are provided based on written agreements, 
                  quotations, or purchase orders accepted by both parties.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>3.2 Scope of Work:</strong> The scope of each survey or inspection will be 
                  clearly defined in the service agreement. Any changes to the scope must be agreed 
                  upon in writing.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <strong>3.3 Access:</strong> The client must provide safe and timely access to vessels, 
                  equipment, and documentation required for the survey or inspection.
                </p>
              </section>

              {/* 4. Reports and Findings */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">4. Reports and Findings</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>4.1 Report Delivery:</strong> Survey reports will be delivered within the 
                  agreed timeframe, typically 3-5 business days unless otherwise specified.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>4.2 Accuracy:</strong> While we strive for accuracy in all reports, our 
                  findings are based on visual inspection and available information at the time of survey.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <strong>4.3 Confidentiality:</strong> All survey reports and findings are confidential 
                  and will only be shared with the client or authorized parties as specified in the service agreement.
                </p>
              </section>

              {/* 5. Fees and Payment */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">5. Fees and Payment</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>5.1 Pricing:</strong> Service fees are quoted based on the scope of work 
                  and will be confirmed in writing before commencement.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>5.2 Payment Terms:</strong> Unless otherwise agreed, payment is due within 
                  20 days of invoice date. Late payments may incur additional charges.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>5.3 Cancellation:</strong> Cancellations made less than 24 hours before 
                  scheduled service may incur a cancellation fee.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <strong>5.4 Banking and Transaction Charges:</strong> Any banking fees, wire transfer 
                  charges, currency conversion fees, or other financial transaction costs incurred during 
                  payment processing shall be borne by the client. All payments must be made in full 
                  without deduction of bank charges, ensuring that Gamma Marine Limited receives the 
                  complete invoiced amount.
                </p>
              </section>

              {/* 6. Liability and Insurance */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">6. Liability and Insurance</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>6.1 Professional Indemnity:</strong> Gamma Marine Limited maintains 
                  professional indemnity insurance as required by Maritime New Zealand.
                </p>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>6.2 Limitation of Liability:</strong> Gamma Marine Limited's attendance at 
                  all Surveys is undertaken Without Prejudice and Without Liability of ourselves or for 
                  any Party for whom we act; however, in the event that liability is found to attach, 
                  such liability shall be limited to the fees paid for the specific service in question, 
                  except where prohibited by law.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <strong>6.3 Client Responsibility:</strong> The client is responsible for decisions 
                  made based on our reports and recommendations.
                </p>
              </section>

              {/* 7. Intellectual Property */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">7. Intellectual Property</h2>
                <p className="text-gray-600 leading-relaxed">
                  All reports, documents, and materials produced by Gamma Marine Limited remain our 
                  intellectual property. Clients are granted a license to use these materials for their 
                  intended purpose but may not reproduce or distribute them without written consent.
                </p>
              </section>

              {/* 8. Health and Safety */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">8. Health and Safety</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>8.1 Compliance:</strong> All work is conducted in compliance with New Zealand 
                  Health and Safety at Work Act 2015 and maritime safety regulations.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <strong>8.2 Safety Requirements:</strong> Clients must ensure vessels and facilities 
                  meet necessary safety standards for surveyors to conduct inspections.
                </p>
              </section>

              {/* 9. Privacy and Data Protection */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">9. Privacy and Data Protection</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  We collect and process personal information in accordance with the New Zealand Privacy 
                  Act 2020. Client information is stored securely and used only for service delivery and 
                  business purposes.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  Personal information will not be shared with third parties except as required by law 
                  or with explicit consent.
                </p>
              </section>

              {/* 10. Dispute Resolution */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">10. Dispute Resolution</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  <strong>10.1 Good Faith:</strong> Both parties agree to attempt to resolve any disputes 
                  through good faith negotiation.
                </p>
                <p className="text-gray-600 leading-relaxed">
                  <strong>10.2 Jurisdiction:</strong> These Terms are governed by New Zealand law, and 
                  disputes will be subject to the jurisdiction of New Zealand courts.
                </p>
              </section>

              {/* 11. Changes to Terms */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">11. Changes to Terms</h2>
                <p className="text-gray-600 leading-relaxed">
                  We reserve the right to modify these Terms at any time. Changes will be effective 
                  immediately upon posting to our website. Continued use of our services after changes 
                  constitutes acceptance of the modified Terms.
                </p>
              </section>

              {/* 12. Contact Information */}
              <section className="mb-8">
                <h2 className="text-2xl font-bold text-maritime-primary mb-4">12. Contact Information</h2>
                <p className="text-gray-600 leading-relaxed mb-4">
                  For questions about these Terms and Conditions, please contact us:
                </p>
                <div className="bg-maritime-primary/5 p-6 rounded-lg">
                  <p className="text-gray-700 font-semibold mb-2">Gamma Marine Limited</p>
                  <p className="text-gray-600 mb-1">Tauranga, Bay of Plenty Region, New Zealand</p>
                  <p className="text-gray-600 mb-1">Phone: +64 21 2314953</p>
                  <p className="text-gray-600 mb-1">Email: ops@gamma-marine.co.nz</p>
                </div>
              </section>

              {/* Acceptance */}
              <Card className="mt-12 bg-maritime-primary text-white">
                <CardContent className="p-8">
                  <h3 className="text-xl font-bold mb-4">Acceptance of Terms</h3>
                  <p className="leading-relaxed">
                    By using our services or website, you acknowledge that you have read, understood, 
                    and agree to be bound by these Terms and Conditions. If you do not agree with these 
                    terms, please contact our team.
                  </p>
                </CardContent>
              </Card>

            </div>

            {/* Back to Home */}
            <div className="mt-12 text-center">
              <Link to="/">
                <Button 
                  variant="outline" 
                  className="border-2 border-maritime-primary text-maritime-primary hover:bg-maritime-primary hover:text-white"
                >
                  <ArrowLeft className="mr-2 w-4 h-4" />
                  Back to Home
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
